import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import * as api from '../services/api';

const initialState = {
  leads: [],
  leadsTotal: 0,
  leadsPage: 1,
  leadsPages: 1,
  stats: null,
  pipelines: [],
  fields: [],
  activities: [],
  attachments: [],
  meetings: [],
  alerts: [],
  selectedPipeline: null,
  loading: true,
  error: null,
};

const dataReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'SET_LEADS':
      return { 
        ...state, 
        leads: action.payload.leads || action.payload,
        leadsTotal: action.payload.total || state.leadsTotal,
        leadsPage: action.payload.page || state.leadsPage,
        leadsPages: action.payload.pages || state.leadsPages
      };
    case 'SET_STATS':
      return { ...state, stats: action.payload };
    case 'ADD_LEAD':
      return { 
        ...state, 
        leads: [action.payload, ...state.leads],
        leadsTotal: state.leadsTotal + 1
      };
    case 'UPDATE_LEAD':
      return {
        ...state,
        leads: state.leads.map(lead =>
          lead.id === action.payload.id ? { ...lead, ...action.payload.updates } : lead
        ),
      };
    case 'DELETE_LEAD':
      return { ...state, leads: state.leads.filter(lead => lead.id !== action.payload) };
    case 'SET_PIPELINES':
      return { ...state, pipelines: action.payload };
    case 'ADD_PIPELINE':
      return { ...state, pipelines: [...state.pipelines, action.payload] };
    case 'UPDATE_PIPELINE':
      return {
        ...state,
        pipelines: state.pipelines.map(pipeline =>
          pipeline.id === action.payload.id ? { ...pipeline, ...action.payload.updates } : pipeline
        ),
      };
    case 'DELETE_PIPELINE':
      return { ...state, pipelines: state.pipelines.filter(pipeline => pipeline.id !== action.payload) };
    case 'SET_FIELDS':
      return { ...state, fields: action.payload };
    case 'ADD_FIELD':
      return { ...state, fields: [...state.fields, action.payload] };
    case 'SET_ACTIVITIES':
      return { ...state, activities: action.payload };
    case 'ADD_ACTIVITY':
      return { ...state, activities: [...state.activities, action.payload] };
    case 'SET_ATTACHMENTS':
      return { ...state, attachments: action.payload };
    case 'ADD_ATTACHMENT':
      return { ...state, attachments: [...state.attachments, action.payload] };
    case 'SET_MEETINGS':
      return { ...state, meetings: action.payload };
    case 'ADD_MEETING':
      return { ...state, meetings: [...state.meetings, action.payload] };
    case 'SET_ALERTS':
      return { ...state, alerts: action.payload };
    case 'ADD_ALERT':
      return { ...state, alerts: [...state.alerts, action.payload] };
    case 'UPDATE_ALERT':
      return {
        ...state,
        alerts: state.alerts.map(alert =>
          alert.id === action.payload.id ? { ...alert, ...action.payload.updates } : alert
        ),
      };
    case 'SET_SELECTED_PIPELINE':
      return { ...state, selectedPipeline: action.payload };
    case 'LOAD_DATA_SUCCESS':
      return {
        ...state,
        ...action.payload,
        loading: false,
        error: null,
      };
    default:
      return state;
  }
};

const DataContext = createContext(undefined);

export const DataProvider = ({ children }) => {
  const [state, dispatch] = useReducer(dataReducer, initialState);

  // Load all data from API
  const loadData = useCallback(async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      // Load only essential data first (pipelines for navigation)
      const pipelines = await api.getPipelines();
      
      // Dispatch pipelines immediately so UI can render
      dispatch({
        type: 'LOAD_DATA_SUCCESS',
        payload: { 
          leads: [],
          leadsTotal: 0,
          leadsPage: 1,
          leadsPages: 1,
          stats: null,
          pipelines, 
          fields: [],
          alerts: [],
          activities: [],
          meetings: [],
          attachments: []
        },
      });
      
      // Load stats in background (for dashboard numbers)
      api.getLeadsStats().then(stats => {
        dispatch({ type: 'SET_STATS', payload: stats });
      }).catch(console.error);
      
      // Load first few leads for recent leads section
      api.getLeads(1, 10).then(data => {
        dispatch({ type: 'SET_LEADS', payload: data });
      }).catch(console.error);
      
    } catch (error) {
      console.error('Error loading data:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (state.pipelines.length > 0 && !state.selectedPipeline) {
      dispatch({ type: 'SET_SELECTED_PIPELINE', payload: state.pipelines[0].id });
    }
  }, [state.pipelines, state.selectedPipeline]);

  // API Actions
  const actions = {
    // Leads
    loadLeads: async (page = 1, limit = 50, pipelineId = null, search = null) => {
      try {
        const data = await api.getLeads(page, limit, pipelineId, search);
        dispatch({ type: 'SET_LEADS', payload: data });
        return data;
      } catch (error) {
        console.error('Error loading leads:', error);
        throw error;
      }
    },
    loadLeadsByPipeline: async (pipelineId, limitPerStage = 50) => {
      try {
        const data = await api.getLeadsByPipeline(pipelineId, limitPerStage);
        return data;
      } catch (error) {
        console.error('Error loading pipeline leads:', error);
        throw error;
      }
    },
    addLead: async (leadData) => {
      try {
        const lead = await api.createLead(leadData);
        dispatch({ type: 'ADD_LEAD', payload: lead });
        return lead;
      } catch (error) {
        console.error('Error adding lead:', error);
        throw error;
      }
    },
    updateLead: async (leadId, updates) => {
      try {
        const updatedLead = await api.updateLead(leadId, updates);
        dispatch({ type: 'UPDATE_LEAD', payload: { id: leadId, updates } });
        return updatedLead; // Return updated lead data
      } catch (error) {
        console.error('Error updating lead:', error);
        throw error;
      }
    },
    deleteLead: async (leadId) => {
      try {
        await api.deleteLead(leadId);
        dispatch({ type: 'DELETE_LEAD', payload: leadId });
      } catch (error) {
        console.error('Error deleting lead:', error);
        throw error;
      }
    },
    
    // Activities
    addActivity: async (activityData) => {
      try {
        const activity = await api.createActivity(activityData);
        dispatch({ type: 'ADD_ACTIVITY', payload: activity });
        return activity;
      } catch (error) {
        console.error('Error adding activity:', error);
        throw error;
      }
    },
    
    // Alerts
    addAlert: async (alertData) => {
      try {
        const alert = await api.createAlert(alertData);
        dispatch({ type: 'ADD_ALERT', payload: alert });
        return alert;
      } catch (error) {
        console.error('Error adding alert:', error);
        throw error;
      }
    },
    updateAlert: async (alertId, updates) => {
      try {
        await api.updateAlert(alertId, updates);
        dispatch({ type: 'UPDATE_ALERT', payload: { id: alertId, updates } });
      } catch (error) {
        console.error('Error updating alert:', error);
        throw error;
      }
    },
    
    // Meetings
    addMeeting: async (meetingData) => {
      try {
        const meeting = await api.createMeeting(meetingData);
        dispatch({ type: 'ADD_MEETING', payload: meeting });
        return meeting;
      } catch (error) {
        console.error('Error adding meeting:', error);
        throw error;
      }
    },
    
    // Attachments
    addAttachment: async (attachmentData) => {
      try {
        const attachment = await api.createAttachment(attachmentData);
        dispatch({ type: 'ADD_ATTACHMENT', payload: attachment });
        return attachment;
      } catch (error) {
        console.error('Error adding attachment:', error);
        throw error;
      }
    },
    
    // Pipelines
    addPipeline: async (pipelineData) => {
      try {
        const pipeline = await api.createPipeline(pipelineData);
        dispatch({ type: 'ADD_PIPELINE', payload: pipeline });
        return pipeline;
      } catch (error) {
        console.error('Error adding pipeline:', error);
        throw error;
      }
    },
    updatePipeline: async (pipelineId, updates) => {
      try {
        await api.updatePipeline(pipelineId, updates);
        dispatch({ type: 'UPDATE_PIPELINE', payload: { id: pipelineId, updates } });
      } catch (error) {
        console.error('Error updating pipeline:', error);
        throw error;
      }
    },
    deletePipeline: async (pipelineId) => {
      try {
        await api.deletePipeline(pipelineId);
        dispatch({ type: 'DELETE_PIPELINE', payload: pipelineId });
      } catch (error) {
        console.error('Error deleting pipeline:', error);
        throw error;
      }
    },
    
    // Refresh data
    refreshData: loadData,
  };

  return (
    <DataContext.Provider value={{ state, dispatch, actions }}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within DataProvider');
  }
  return context;
};
